import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import styled from 'styled-components';

import { Link } from 'core/routing';

import Button from 'components/Button';
import Loading from 'components/Loading';

import { Center, StyledLink } from 'legacyComponents/styled/lib';

import { BlockPreview } from 'libs/editor/styles';
import { FilePreviewer } from 'libs/rendering/preview';
import { viewerURLtoFilename, viewerURLToParams } from 'libs/rendering/utils';

import { RenderNode } from 'libs/editor/plugins/helpers/renderNode';

@inject('routerStore')
@observer
export class PreviewBlockNode extends React.Component {
  render() {
    const {
      node,
      isSelected,
      attributes,
      readOnly,
      routerStore,
      editor,
    } = this.props;

    const { previewMode, previewDownloadButtonHidden } = editor.props;
    const href = node.data.get('href');
    const fileManager = node.data.get('fileManager');
    const { route } = routerStore;

    let name = '';
    let basepath = '';
    let goToButton = '';
    let downloadButton = '';
    let displayType = '';
    let previewType = 'Upload';

    let uploadOverlay = null;

    let embedType = '';

    if (href) {
      name = viewerURLtoFilename(href);
      const typeParams = viewerURLToParams(href);

      embedType = typeParams?.name;

      previewType = typeParams.type;
      if (previewType === 'Embed') {
        displayType = '(latest)';
      } else if (previewType === 'Versioned Embed') {
        displayType = `(#${typeParams.params.contributionId})`;
      }
      const contextPath =
        typeParams.contextPath && !route.name.includes('.project.')
          ? `${typeParams.contextPath}/`
          : null;

      let updatedBasePath = typeParams.params.basepath;
      if (embedType === 'profile.shared.file') {
        name = typeParams.params.profile;
        updatedBasePath = typeParams.params.sharedFileId;
      }

      if (typeParams.params && typeParams.path) {
        const url = new URL(href);

        basepath = (
          <p className='basePath'>
            <StyledLink
              name={name}
              params={{ ...typeParams.params, viaPreview: 'true' }}
            >
              {contextPath}
              {updatedBasePath}
            </StyledLink>
          </p>
        );

        goToButton = (
          <Link
            name={typeParams.name}
            params={{ ...typeParams.params, viaPreview: 'true' }}
          >
            <ButtonStyled variant='secondary' icon='launch' size='small' />
          </Link>
        );

        downloadButton = (
          <AStyled
            href={`${url.origin}/files/${typeParams.params.fileID}`}
            target='_blank'
            rel='noopener noreferrer'
            download
          >
            <Button variant='secondary' icon='download' size='small'></Button>
          </AStyled>
        );
      }
    } else if (fileManager && fileManager.file) {
      name = fileManager.file.filename;
      if (!fileManager.file.completed) {
        if (previewMode) {
          displayType = '';
          uploadOverlay = (
            <PercentageHolder>
              {`${Math.round(fileManager.uploadProgress * 100) / 100}%`}
            </PercentageHolder>
          );
        } else {
          displayType = 'Uploading...';
          uploadOverlay = (
            <UploadingOverlay>
              <LoadingWrapper>
                <Loading
                  text={`${Math.round(fileManager.uploadProgress * 100) /
                    100}%`}
                />
              </LoadingWrapper>
            </UploadingOverlay>
          );
        }
      }
    }

    if (previewDownloadButtonHidden) {
      downloadButton = null;
    }

    return (
      <BlockPreview isSelected={!readOnly && isSelected} {...attributes}>
        <FilePreviewer
          viewerURL={href}
          previewMode={previewMode}
          allowViewer
          editorMode
        >
          <InfoHeader>
            <FileInfo>
              <p className='fileName'>
                {name} <span>{displayType}</span>
              </p>
              {basepath}

              {previewMode &&
              previewMode === 'chat' &&
              embedType !== 'profile.shared.file'
                ? downloadButton
                : goToButton}
            </FileInfo>
          </InfoHeader>
          {uploadOverlay}
        </FilePreviewer>
      </BlockPreview>
    );
  }
}

PreviewBlockNode.propTypes = {
  attributes: PropTypes.object,
  node: PropTypes.object,
  isSelected: PropTypes.bool,
  readOnly: PropTypes.bool,
  routerStore: PropTypes.object,
};

export function PreviewBlock() {
  return {
    uploadButton: {
      elementType: 'block',
      type: 'upload',
      icon: 'upload',
    },
    button: {
      elementType: 'block',
      type: 'reference',
      icon: 'embed',
    },
    plugins: [
      RenderNode('preview-block', props => <PreviewBlockNode {...props} />),
    ],
  };
}

const UploadingOverlay = styled(Center)`
  z-index: 1;
  background-color: ${props => props.theme.color.wfWhite};
  height: 100%;
  width: 100%;
  position: absolute;
`;

const PercentageHolder = styled.div`
  z-index: 1;
  width: 100%;
  position: absolute;
  padding: 0.5em;
  text-align: right;
`;

const LoadingWrapper = styled.div`
  width: 100%;
  height: 100%;
  transform: translateY(0.5em);
`;

const InfoHeader = styled.div`
  z-index: 2;
  padding: 10px 15px;
  border-bottom: 1px solid ${props => props.theme.color.wfGrey200};
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  > div > p {
    margin: 0px;
    font-size: 15px;
  }
  > div > p.fileName {
    font-weight: 600;
    color: ${props => props.theme.color.wfBlack};
    > span {
      font-weight: ${props => props.theme.font.weight.regular};
      color: ${props => props.theme.color.wfBlack300};
    }
  }
  > div > p.basePath {
    font-size: 12px;
    margin-top: 2px;
    z-index: 1;
    position: relative;
  }
  .typeBadge {
    background-color: ${props => props.theme.color.wfGrey200};
    border: 1px solid ${props => props.theme.color.wfGrey300};
    float: right;
    font-size: 0.6em;
    color: ${props => props.theme.color.wfGrey900};
    padding: 4px 10px;
  }
`;

const FileInfo = styled.div`
  display: inline-block;
`;

const ButtonStyled = styled(Button)`
  position: absolute;
  top: 10px;
  right: 15px;
  z-index: 1;
`;

const AStyled = styled.a`
  position: absolute;
  top: 10px;
  right: 15px;
  z-index: 1;
`;
